import { Link, useParams } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import Breadcrumb from '@/components/base/Breadcrumb';
import RatingStars from '@/components/base/RatingStars';
import SectionHeading from '@/components/base/SectionHeading';
import MovieCard from '@/components/base/MovieCard';
import ReviewList from './components/ReviewList';

export default function MovieDetail() {
  const { id } = useParams();
  const movie = movies.find((m) => m.id === id);

  if (!movie) {
    return (
      <div className="max-w-content mx-auto px-4 md:px-6 py-20 md:py-32 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
          <i className="ri-film-line text-3xl"></i>
        </span>
        <h1 className="mt-5 text-xl md:text-2xl font-bold text-foreground-950">未找到该电影</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的电影可能已被移除或链接有误。</p>
        <Link
          to="/movies"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
          返回电影列表
        </Link>
      </div>
    );
  }

  const related = movies
    .filter((m) => m.id !== movie.id && m.genres.some((g) => movie.genres.includes(g)))
    .slice(0, 4);

  return (
    <div className="w-full">
      {/* 顶部大图封面 */}
      <section className="relative bg-primary-950">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={movie.backdrop}
            alt={`${movie.title}剧照`}
            title={`${movie.title} - 剧情介绍与影评`}
            className="w-full h-full object-cover object-top opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/40"></div>
        </div>

        <div className="relative max-w-content mx-auto px-4 md:px-6 py-8 md:py-14">
          <Breadcrumb
            className="mb-6 [&_a]:text-background-200/80 [&_a:hover]:text-accent-400 [&_span]:text-background-50"
            items={[{ label: '首页', to: '/' }, { label: '电影列表', to: '/movies' }, { label: movie.title }]}
          />

          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 md:w-52 shrink-0 mx-auto md:mx-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-foreground-800">
                <img
                  src={movie.poster}
                  alt={`${movie.title}电影海报`}
                  title={`${movie.title} - 完整剧情解析`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0 text-center md:text-left">
              <h1 className="text-2xl md:text-4xl font-bold text-white">{movie.title}</h1>
              <p className="mt-2 text-sm md:text-base text-background-200/75">{movie.enTitle}</p>

              <div className="mt-4 flex items-center justify-center md:justify-start gap-3 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-sm font-bold">
                  {movie.rating.toFixed(1)} 分
                </span>
                <RatingStars rating={movie.rating} showValue={false} size="md" />
                <span className="text-sm text-background-200/80">{movie.year} 年</span>
                <span className="text-sm text-background-200/80">{movie.duration} 分钟</span>
                <span className="text-sm text-background-200/80">{movie.region}</span>
              </div>

              <p className="mt-5 max-w-2xl mx-auto md:mx-0 text-sm md:text-base text-background-100/90 leading-relaxed">
                {movie.tagline}
              </p>

              <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm max-w-2xl mx-auto md:mx-0">
                <div className="flex gap-2">
                  <dt className="text-background-200/60 shrink-0">导演</dt>
                  <dd className="text-background-50">{movie.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-background-200/60 shrink-0">类型</dt>
                  <dd className="text-background-50">{movie.genres.join(' / ')}</dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="text-background-200/60 shrink-0">主演</dt>
                  <dd className="text-background-50">{movie.cast.join('、')}</dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-wrap gap-2 justify-center md:justify-start">
                {movie.tags.map((t) => (
                  <span
                    key={t}
                    className="px-2.5 py-1 rounded-md bg-background-50/10 text-background-100 text-xs border border-background-50/15"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 剧情介绍 */}
      <section className="max-w-content mx-auto px-4 md:px-6 py-10 md:py-14">
        <SectionHeading title="剧情介绍" subtitle="故事梗概，不含关键结局剧透" />
        <div className="mt-6 max-w-3xl">
          <p className="text-base leading-8 text-foreground-700 whitespace-pre-line">
            {movie.synopsis}
          </p>
        </div>
      </section>

      {/* 专业影评 */}
      <section className="bg-background-100 border-y border-background-200">
        <div className="max-w-content mx-auto px-4 md:px-6 py-10 md:py-14">
          <SectionHeading
            title="专业影评"
            subtitle={`共 ${movie.reviews.length} 条精选观点，观点仅代表作者个人立场`}
          />
          <div className="mt-6">
            <ReviewList reviews={movie.reviews} />
          </div>
        </div>
      </section>

      {/* 相关推荐 */}
      {related.length > 0 && (
        <section className="max-w-content mx-auto px-4 md:px-6 py-10 md:py-14">
          <SectionHeading
            title="相关推荐"
            subtitle="与本部影片题材或风格相近的作品"
            action={
              <Link
                to="/movies"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 inline-flex items-center gap-1 whitespace-nowrap"
              >
                更多电影
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            }
          />
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {related.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}